// MUI
import { Badge, Box, Drawer, IconButton, ListItemButton, styled } from "@mui/material";

// Third party imports
// Utils
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED } from "layouts/_shared/shellMetrics";

import { colors, shadows, zIndex } from "styles/tokens";

// Props that are only styling hints (or handled by the styled wrapper) must not reach the DOM
const STYLE_ONLY_PROPS = new Set([
  "open",
  "mobile",
  "expanded",
  "active",
  "ownerState",
  "theme",
  "sx",
  "as",
]);
const forwardProp = (prop) => !STYLE_ONLY_PROPS.has(prop);

const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";

// Below 768 (mobile) the sidebar is an overlay drawer: this column takes no room (width 0).
// Its width moves with the same duration/easing as the paper, so the content does not jump.
const widthTransition = (theme) =>
  theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });

export const SidebarStyled = styled(Box, { shouldForwardProp: forwardProp })(({
  theme,
  open,
  mobile,
}) => {
  let width = SIDEBAR_WIDTH_COLLAPSED;
  if (mobile) width = 0;
  else if (open) width = SIDEBAR_WIDTH;
  return {
    position: "relative",
    flexShrink: 0,
    width,
    backgroundColor: "transparent",
    transition: widthTransition(theme),
    [REDUCED_MOTION]: { transition: "none" },
  };
});

// Count of unread notifications on the collapsed sidebar icon. White on #1958B3 is 6.4:1.
export const StyledBadge = styled(Badge)({
  "& .MuiBadge-badge": {
    backgroundColor: colors.primaryStrong,
    color: colors.white,
    fontSize: 11,
    fontWeight: 600,
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    boxShadow: `0 0 0 2px ${colors.gray50}`,
  },
});

// Same count, inline at the end of an expanded row
export const CountPill = styled("span")({
  marginLeft: "auto",
  minWidth: 20,
  padding: "1px 6px",
  borderRadius: 10,
  backgroundColor: colors.primaryStrong,
  color: colors.white,
  fontSize: 11,
  fontWeight: 600,
  lineHeight: "16px",
  textAlign: "center",
});

// Permanent from 768 up (collapsed or expanded); temporary overlay below (mobile)
// `open` reaches the Drawer (the temporary one needs it); `expanded` and `mobile` are style only.
export const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop === "open" || forwardProp(prop),
})(({ theme, expanded, mobile }) => {
  const width = expanded ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_COLLAPSED;
  const paper = {
    backgroundColor: colors.gray50,
    borderRight: `1px solid ${colors.gray200}`,
    overflowX: "hidden",
    boxSizing: "border-box",
    whiteSpace: "nowrap",
  };
  if (mobile) {
    // The Modal root is fixed full screen: only the paper has a width. Above the AI FAB.
    return {
      zIndex: zIndex.modal,
      "& .MuiDrawer-paper": { ...paper, width: SIDEBAR_WIDTH, maxWidth: "calc(100vw - 48px)" },
    };
  }
  return {
    width,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    transition: widthTransition(theme),
    [REDUCED_MOTION]: { transition: "none" },
    "& .MuiDrawer-paper": {
      ...paper,
      width,
      zIndex: zIndex.sidebar,
      transition: widthTransition(theme),
      [REDUCED_MOTION]: { transition: "none" },
    },
  };
});

export const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
}));

export const LogoContainer = styled(Box, { shouldForwardProp: forwardProp })({
  position: "relative",
  width: "100%",
  height: "40px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 8,
  "&:focus-visible": { outline: `2px solid ${colors.primary}`, outlineOffset: 2 },
});

// Sits half outside the sidebar, over the navbar, hence appBar + 1. A small IconButton (28, r8).
export const ToggleButton = styled(IconButton, { shouldForwardProp: forwardProp })(({ theme }) => ({
  width: 28,
  height: 28,
  padding: 0,
  backgroundColor: colors.white,
  border: `1px solid ${colors.gray200}`,
  boxShadow: shadows.card,
  color: colors.gray500,
  zIndex: zIndex.appBar + 1,
  position: "absolute",
  top: 18,
  right: 0,
  transform: "translateX(50%)",
  fontSize: 16,
  "&:hover": { backgroundColor: colors.white, color: colors.primary, borderColor: colors.gray300 },
  "&:focus-visible": { outline: `2px solid ${colors.primary}`, outlineOffset: 2 },
  transition: theme.transitions.create(["right", "transform", "color", "border-color"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shortest,
  }),
  [REDUCED_MOTION]: { transition: "none" },
}));

// A row of the sidebar: rounded-lg, selected = primary-soft background + primary text
export const StyledListItemButton = styled(ListItemButton, { shouldForwardProp: forwardProp })(
  ({ theme, active }) => ({
    backgroundColor: active ? colors.primarySoft : "transparent",
    color: active ? colors.primaryStrong : colors.gray600,
    borderRadius: 8,
    minHeight: 36,
    padding: theme.spacing(0.75, 1.25),
    margin: 0,
    width: "100%",
    gap: theme.spacing(1.5),
    transition: theme.transitions.create(["background-color", "color"], {
      duration: theme.transitions.duration.shortest,
    }),
    "&:hover": {
      backgroundColor: active ? colors.primarySoft : colors.gray100,
      color: active ? colors.primaryStrong : colors.gray900,
    },
    "&.Mui-focusVisible": {
      backgroundColor: active ? colors.primarySoft : colors.gray100,
      outline: `2px solid ${colors.primary}`,
      outlineOffset: -2,
    },
    "& .MuiListItemText-primary": {
      fontSize: 14,
      fontWeight: active ? 600 : 500,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    [REDUCED_MOTION]: { transition: "none" },
  })
);

// Icon slot of a row: 20px, takes the colour of the row
export const rowIconSx = (open) => ({
  minWidth: 0,
  width: 20,
  height: 20,
  justifyContent: "center",
  color: "inherit",
  ...(open ? {} : { mx: "auto" }),
  "& svg": { width: 20, height: 20 },
});

// <ListItem> that wraps a row: the side gap lives here, not on the button
export const rowListItemSx = { display: "block", px: 1.5, py: 0.25 };

// Small title above a group of rows
export const sectionTitleSx = {
  px: 3,
  pt: 2.5,
  pb: 0.75,
};
